import React, { useEffect } from 'react'
import type { SEOMetaTagsProps } from '../types/seo'
import { getAbsoluteAssetUrl } from '../utils/seo'

/**
 * SEO Meta Tags Component
 *
 * Handles comprehensive SEO meta tags including:
 * - Page title and description
 * - Canonical URLs
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - JSON-LD structured data
 * - Robots directives
 */
const SEOMetaTags: React.FC<SEOMetaTagsProps> = ({
  title,
  description,
  canonical,
  ogImage,
  keywords,
  noIndex = false,
  schemas,
}) => {
  useEffect(() => {
    // Set page title
    document.title = title

    // Remove all existing SEO meta tags to avoid duplicates
    const metaSelectors = [
      'meta[name="description"]',
      'meta[name="keywords"]',
      'meta[name="robots"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]',
      'link[rel="canonical"]',
      'script[type="application/ld+json"]',
    ]

    metaSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => el.remove())
    })

    // Meta description
    const descriptionMeta = document.createElement('meta')
    descriptionMeta.name = 'description'
    descriptionMeta.content = description
    document.head.appendChild(descriptionMeta)

    // Keywords (if provided)
    if (keywords && keywords.length > 0) {
      const keywordsMeta = document.createElement('meta')
      keywordsMeta.name = 'keywords'
      keywordsMeta.content = keywords.join(', ')
      document.head.appendChild(keywordsMeta)
    }

    // Robots meta tag
    const robotsMeta = document.createElement('meta')
    robotsMeta.name = 'robots'
    robotsMeta.content = noIndex ? 'noindex, follow' : 'index, follow'
    document.head.appendChild(robotsMeta)

    // Canonical link tag
    const canonicalLink = document.createElement('link')
    canonicalLink.rel = 'canonical'
    canonicalLink.href = canonical
    document.head.appendChild(canonicalLink)

    // Open Graph meta tags
    const ogImageUrl = getAbsoluteAssetUrl(ogImage)

    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: ogImageUrl },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      {
        property: 'og:image:alt',
        content:
          'NCESP - Because children deserve a relationship with both parents',
      },
      {
        property: 'og:site_name',
        content: 'National Council for Equal Shared Parenting',
      },
      { property: 'og:locale', content: 'en_US' },
    ]

    ogTags.forEach(({ property, content }) => {
      const meta = document.createElement('meta')
      meta.setAttribute('property', property)
      meta.content = content
      document.head.appendChild(meta)
    })

    // Twitter Card meta tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImageUrl },
      {
        name: 'twitter:image:alt',
        content:
          'NCESP - Because children deserve a relationship with both parents',
      },
    ]

    twitterTags.forEach(({ name, content }) => {
      const meta = document.createElement('meta')
      meta.name = name
      meta.content = content
      document.head.appendChild(meta)
    })

    // JSON-LD structured data (if schemas provided)
    if (schemas && schemas.length > 0) {
      schemas.forEach((schema, index) => {
        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.text = JSON.stringify(schema)
        script.setAttribute('data-schema-index', String(index))
        document.head.appendChild(script)
      })
    }

    // Cleanup function
    return () => {
      // Cleanup is handled by removing tags at the start of next effect
    }
  }, [title, description, canonical, ogImage, keywords, noIndex, schemas])

  // This component doesn't render anything visible
  return null
}

export default SEOMetaTags
